/**
 * Couches de sites et filtre des sites d'une maille. Sans dépendance à la carte :
 * testé sous Node tel quel.
 *
 * Le filtre n'est pas une option : c'est l'ensemble des types des couches chargées.
 * `null` : aucune couche de sites configurée, donc aucun filtre (comportement 1.4.0).
 */
export type FiltreSites = ReadonlySet<string> | null

/** Une couche de sites : un fichier GeoJSON, et au besoin son type, son libellé, sa couleur. */
export interface CoucheSites {
  url: string
  /** Type de tous les sites du fichier. Sinon : propriété `type_site` (ou `type`) de chaque site. */
  type?: string
  libelle?: string
  couleur?: string
}

export const normaliserType = (type: unknown): string => String(type ?? '').trim().toUpperCase()

/**
 * `couches.sites` : une URL, ou une liste d'URL et d'objets `{ url, type?, libelle?, couleur? }`.
 * Rend `null` si l'option est absente.
 */
export function lireCouchesSites(valeur: unknown): CoucheSites[] | null {
  if (valeur === undefined || valeur === null || valeur === '') return null
  const liste = Array.isArray(valeur) ? valeur : [valeur]
  return liste.map((c, i) => {
    if (typeof c === 'string' && c.trim() !== '') return { url: c.trim() }
    if (c && typeof c === 'object' && typeof (c as CoucheSites).url === 'string' && (c as CoucheSites).url.trim() !== '') {
      const o = c as CoucheSites
      const texte = (v: unknown) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined)
      return { url: o.url.trim(), type: texte(o.type), libelle: texte(o.libelle), couleur: texte(o.couleur) }
    }
    throw new TypeError(`couches.sites[${i}] : une URL, ou { url, type?, libelle?, couleur? }, était attendue`)
  })
}

export function typeRetenu(type: unknown, filtre: FiltreSites): boolean {
  return filtre === null || filtre.has(normaliserType(type))
}

/** Les sites d'une maille dont le type fait partie des couches chargées. Chaque site porte `type` ou `type_site`. */
export function filtrerSites<T>(sites: readonly T[], filtre: FiltreSites): T[] {
  if (filtre === null) return [...sites]
  return sites.filter((s) => {
    const o = (s && typeof s === 'object' ? s : {}) as Record<string, unknown>
    return typeRetenu(o.type ?? o.type_site, filtre)
  })
}
